// src/components/partner/BalanceCard.tsx

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AddCircleOutline, Wallet } from '@mui/icons-material';

import { toUsdDisplay } from '@utils/money';

type SectionWrapperArgs = {
  title: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  description?: string;
  highlighted?: boolean;
  children: React.ReactNode;
};

type Props = {
  balance: number;
  deposit: number;
  setDeposit: (value: number) => void;
  onApplyDeposit: () => Promise<void> | void;
  applying?: boolean;
  sectionWrapper: (args: SectionWrapperArgs) => React.ReactElement;
};

const DEPOSIT_HELP_TEXT = 'Deposits are applied immediately and will appear in Recent Transactions.';

// MUI v5 small TextField (Outlined) input height is typically 40px.
const INPUT_HEIGHT_SM = 40;

/**
 * Balance card
 * - Shows the current balance (read-only)
 * - Allows admins to apply a deposit (server-applied action)
 *
 * Note: Deposit is intentionally excluded from "unsaved changes" highlighting
 * because it is applied immediately via a dedicated action.
 */
const BalanceCard = ({
  balance,
  deposit,
  setDeposit,
  onApplyDeposit,
  applying = false,
  sectionWrapper,
}: Props) => {
  // Keep a string buffer for the input so users can type naturally.
  // Numeric state is synced via setDeposit for parent logic.
  const [depositInput, setDepositInput] = useState<string>(() => (deposit > 0 ? String(deposit) : ''));

  // Keep local buffer consistent if parent value changes externally.
  React.useEffect(() => {
    setDepositInput(deposit > 0 ? String(deposit) : '');
  }, [deposit]);

  const helperTextNode = useMemo(
    () => (
      <Typography variant="caption" color="text.secondary">
        {DEPOSIT_HELP_TEXT}
      </Typography>
    ),
    [],
  );

  const parseDeposit = useCallback((raw: string) => {
    const trimmed = raw.trim();

    // Empty input means 0 deposit (and keep field visually empty).
    if (!trimmed) return { nextInput: '', nextValue: 0 };

    // Allow only non-negative numbers; reject leading zeros like "01".
    // Examples allowed: "0", "0.", "0.5", "10", "10.25"
    if (!/(?!0\d)^\d*\.?\d*$/.test(trimmed)) {
      return null;
    }

    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < 0) return null;

    return { nextInput: trimmed, nextValue: n };
  }, []);

  const handleDepositChange = useCallback(
    (raw: string) => {
      const parsed = parseDeposit(raw);
      if (!parsed) return;

      setDepositInput(parsed.nextInput);
      setDeposit(parsed.nextValue);
    },
    [parseDeposit, setDeposit],
  );

  const canApply = !applying && deposit > 0;

  return sectionWrapper({
    title: 'Balance',
    icon: <Wallet fontSize="small" />,
    children: (
      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <TextField
          label="Current Balance"
          value={toUsdDisplay(balance)}
          size="small"
          fullWidth
          disabled
        />

        {/* Deposit input (with helperText) + Apply button (height matched including helperText area) */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ minWidth: 0 }}>
          {/* Left: input with helper text */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              label="Deposit"
              value={depositInput}
              size="small"
              type="text"
              fullWidth
              placeholder="0"
              disabled={applying}
              onChange={(e) => handleDepositChange(e.target.value)}
              InputProps={{
                inputMode: 'decimal',
                startAdornment: <InputAdornment position="start">USD</InputAdornment>,
              }}
              helperText={helperTextNode}
            />
          </Box>

          {/* Right: button + invisible helperText spacer to match total height */}
          <Box
            sx={{
              flexShrink: 0,
              minWidth: { xs: '100%', md: 180 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
            }}
          >
            <Button
              variant="contained"
              size="small"
              startIcon={
                applying ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutline />
              }
              onClick={onApplyDeposit}
              disabled={!canApply}
              sx={{
                whiteSpace: 'nowrap',
                height: INPUT_HEIGHT_SM,
              }}
            >
              {applying ? 'Applying…' : 'Apply Deposit'}
            </Button>

            {/* Reserve helperText height so the button column matches the TextField column height */}
            <Typography
              variant="caption"
              sx={{
                mt: '3px',
                lineHeight: 1.66,
                visibility: 'hidden',
              }}
            >
              {DEPOSIT_HELP_TEXT}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    ),
  });
};

export default BalanceCard;
